import React, { useState, useEffect } from "react";
import AdminLayout from "@/components/layouts/AdminLayout";
import { marketingService } from "@/services/CRMService";
import {
    Plus,
    Calendar,
    Users,
    Zap,
    Tag,
    Clock,
    Percent,
    ArrowRight,
    Search,
    Filter,
    Gift,
    Sparkles
} from "lucide-react";
import { Button, Badge, CardGlass, StatCard, SectionHeader, TablePro, Skeleton } from "@/components/ui/PremiumComponents";
import { toast } from "sonner";

export default function AdminMarketing() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Create coupon state
    const [newCoupon, setNewCoupon] = useState({
        code: "",
        discount_type: "percent",
        discount_value: 10,
        max_uses: 1,
        expires_at: "",
        segment_target: ""
    });

    useEffect(() => {
        loadCoupons();
    }, []);

    const loadCoupons = async () => {
        setLoading(true);
        try {
            const data = await marketingService.getCoupons();
            setCoupons(data || []);
        } catch (error) {
            console.error(error);
            toast.error("Error al sincronizar campañas de marketing");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCoupon = async (e) => {
        e.preventDefault();
        try {
            await marketingService.createCoupon(newCoupon);
            toast.success("Campaña de cupón activada correctamente");
            setShowCreateModal(false);
            loadCoupons();
            setNewCoupon({ code: "", discount_type: "percent", discount_value: 10, max_uses: 1, expires_at: "", segment_target: "" });
        } catch (error) {
            toast.error("Error al desplegar la nueva campaña");
        }
    };

    return (
        <AdminLayout title="Marketing & Fidelización">
            <div className="max-w-[1400px] mx-auto space-y-8 pb-20 animate-in fade-in duration-700">
                
                <SectionHeader 
                    title="Campañas de Cupones"
                    subtitle="Incentiva el consumo recurrente mediante descuentos estratégicos y promociones personalizadas."
                    actions={
                        <Button className="group" onClick={() => setShowCreateModal(true)}>
                            <Plus className="w-4 h-4 mr-2 transition-transform group-hover:rotate-90" />
                            Crear Cupón
                        </Button>
                    }
                />

                {/* Automation Hub Info */}
                <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 p-6 rounded-3xl relative overflow-hidden flex items-center gap-6">
                    <div className="bg-primary/20 w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 animate-pulse">
                        <Zap className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                        <h4 className="text-white font-black text-sm uppercase tracking-widest mb-1 flex items-center gap-2">
                             Algoritmo de Fidelización Activo <Sparkles className="w-3.5 h-3.5 text-primary" />
                        </h4>
                        <p className="text-slate-400 text-xs max-w-2xl leading-relaxed">
                            El sistema genera cupones automáticos para <b>Cumpleaños (15%)</b>, recuperación de <b>Clientes Inactivos</b> y paquetes de bienvenida para <b>Nuevos Usuarios</b>.
                        </p>
                    </div>
                    <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-primary/5 rounded-full blur-3xl" />
                </div>

                {/* Coupons Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loading ? (
                        [1,2,3].map(i => <div key={i} className="h-64 bg-white/5 rounded-3xl animate-pulse" />)
                    ) : coupons.length > 0 ? (
                        coupons.map((coupon) => (
                            <CardGlass 
                                key={coupon.coupon_id}
                                className={`group hover:border-primary/40 transition-all ${coupon.status !== 'active' ? 'opacity-60 grayscale-[0.5]' : ''}`}
                                headerAction={
                                    <Badge status={coupon.status === 'active' ? 'success' : 'danger'}>
                                        {coupon.status}
                                    </Badge>
                                }
                            >
                                <div className="flex flex-col gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-white/5 rounded-xl border border-white/10 flex items-center justify-center">
                                            <Tag className="w-6 h-6 text-primary group-hover:scale-110 transition-transform" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] uppercase font-black tracking-widest text-slate-500">Código Promo</span>
                                            <span className="text-2xl font-black text-white tracking-tighter group-hover:text-primary transition-colors">
                                                {coupon.code}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between bg-black/30 p-4 rounded-2xl border border-white/5">
                                        <div className="flex flex-col">
                                            <span className="text-[10px] uppercase font-black text-slate-500">Beneficio</span>
                                            <span className="text-xl font-black text-white">
                                                {coupon.discount_type === 'percent' ? `${coupon.discount_value}%` : `€${coupon.discount_value}`} OFF
                                            </span>
                                        </div>
                                        <div className="h-8 w-[1px] bg-white/10" />
                                        <div className="flex flex-col items-end">
                                            <span className="text-[10px] uppercase font-black text-slate-500">Uso</span>
                                            <span className="text-sm font-bold text-slate-300">
                                                {coupon.used_count || 0} / {coupon.max_uses}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="space-y-2 mt-2">
                                        <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500 italic">
                                            <Calendar className="w-3 h-3" />
                                            Expira: {coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : 'Sin Límites'}
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                                            <Users className="w-3 h-3" />
                                            Segmento: {coupon.segment_target || 'Público General'}
                                        </div>
                                    </div>
                                    
                                    <button className="w-full py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs font-black uppercase tracking-widest text-white mt-2 transition-all group-hover:border-primary/20">
                                        Ver Estadísticas <ArrowRight className="w-3 h-3 inline-block ml-2" />
                                    </button>
                                </div>
                            </CardGlass>
                        ))
                    ) : (
                        <div className="col-span-full py-20 bg-white/[0.02] border-2 border-dashed border-white/5 rounded-[40px] flex flex-col items-center justify-center text-center">
                            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mb-6">
                                <Gift className="w-10 h-10 text-slate-700" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Sin campañas activas</h3>
                            <p className="text-slate-500 text-sm max-w-xs mx-auto mb-8">
                                Crea cupones manuales para eventos especiales o deja que el motor automático trabaje por ti.
                            </p>
                            <Button onClick={() => setShowCreateModal(true)}>Configurar Primer Cupón</Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/90 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setShowCreateModal(false)} />
                    <CardGlass 
                        title="Nueva Estrategia de Cupón" 
                        subtitle="Define los parámetros de descuento"
                        className="w-full max-w-md relative z-10 animate-in zoom-in-95 duration-300 shadow-2xl shadow-primary/10"
                    >
                        <form onSubmit={handleCreateCoupon} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest flex items-center gap-2">
                                    <Tag className="w-3 h-3" /> Código Personalizado
                                </label>
                                <input
                                    type="text"
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-white font-black text-lg focus:ring-2 ring-primary/20 transition-all outline-none uppercase placeholder:text-slate-700"
                                    placeholder="EJ: BLACKFRIDAY"
                                    value={newCoupon.code}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                                />
                                <p className="text-[9px] text-slate-500 font-medium">Dejar en blanco para generar uno aleatorio.</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest flex items-center gap-2">
                                        <Percent className="w-3 h-3" /> Tipo
                                    </label>
                                    <select
                                        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-white font-bold outline-none"
                                        value={newCoupon.discount_type}
                                        onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })}
                                    >
                                        <option value="percent">Porcentaje</option>
                                        <option value="fixed">Fijo (€)</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest flex items-center gap-2">
                                        <ArrowRight className="w-3 h-3" /> Valor
                                    </label>
                                    <input
                                        type="number"
                                        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-white font-black outline-none transition-all"
                                        value={newCoupon.discount_value}
                                        onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: parseFloat(e.target.value) })}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Límite de Redenciones</label>
                                <input
                                    type="number"
                                    className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3.5 text-white font-bold outline-none"
                                    value={newCoupon.max_uses}
                                    onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: parseInt(e.target.value) })}
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <Button type="submit" className="flex-1 h-14 uppercase tracking-tighter text-lg">Activar Cupón</Button>
                                <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
                            </div>
                        </form>
                    </CardGlass>
                </div>
            )}
        </AdminLayout>
    );
}
